import { DataFileInterface } from '@systemeio/file-manager';
import { toast } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import { DomainId } from 'modules/profile/mailing-settings/types/mailing-settings-domain-interface';
import { useEffect, useState } from 'react';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useModal } from 'shared/hooks/use-modal';
import { useOnceTrue } from 'shared/hooks/use-once-true';
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules';
import { useCourse } from '../../../hooks/use-course';
import { CourseEditApiInterface, CourseId } from '../../../types/course-interface';
import { useLightCourseActions } from '../../hooks/use-light-course-actions';
import { PatchErrorsLightCourseOptionsInterface } from '../../light-course-interface';
export const useSaveOnSubmit = ({
  courseId
}: {
  courseId?: CourseId;
}) => {
  const {
    t
  } = useLocoTranslation();
  const [isFetching, setIsFetching] = useState(false);
  const [errors, setErrors] = useState<PatchErrorsLightCourseOptionsInterface>();
  const [tempState, setTempState] = useState<Partial<CourseEditApiInterface>>();
  const {
    courseData,
    mutate
  } = useCourse(courseId);
  const {
    patchLightCourseOptions
  } = useLightCourseActions();
  const domainConfirmationModal = useModal<DomainId>();
  const isModalWasOpenOnce = useOnceTrue(domainConfirmationModal.opened);
  const {
    revalidateModules
  } = useRevalidateModules();
  const [isCodeValid, setIsCodeValid] = useState(true);
  useEffect(() => {
    if (courseData) {
      setTempState({
        ...courseData
      });
    } else {
      setTempState(undefined);
    }
  }, [courseData?.id]);
  const handleFormSubmit = async () => {
    if (!tempState) return;
    if (!courseId) return;
    if (isFetching) return;
    if (!isCodeValid) return;
    setIsFetching(true);
    setErrors(undefined);
    try {
      const updatedCourse = await patchLightCourseOptions(courseId, {
        name: tempState.name,
        description: tempState.description,
        embeddedCode: tempState.embeddedCode || null,
        logo: tempState.logo ? tempState.logo.id : null,
        image: tempState.image ? tempState.image.id : null,
        path: tempState.path,
        domainId: tempState.domainId,
        instructorName: tempState.instructorName,
        teacherPhoto: tempState.teacherPhoto ? tempState.teacherPhoto.id : null,
        issueCertificateUponCompletion: tempState.issueCertificateUponCompletion,
        certificateLogo: tempState.certificateLogo ? tempState.certificateLogo.id : null,
        signature: tempState.signature ? tempState.signature.id : null
      });
      await mutate(updatedCourse, false);
      await revalidateModules([RequestModuleEnum.MembershipLightCourse]);
      toast.success(t('global.changes_saved'));
    } catch (error) {
      toast.error(t('global.error'));
      if (error instanceof BadRequest) {
        setErrors(error.errors.fields);
        return;
      }
    } finally {
      setIsFetching(false);
    }
  };
  const handleTeacherPhotoChange = async (file?: DataFileInterface) => {
    setTempState(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        teacherPhoto: file ?? null
      };
    });
  };
  const handleCourseLogoChange = async (file_0?: DataFileInterface) => {
    setTempState(prev_0 => {
      if (!prev_0) return prev_0;
      return {
        ...prev_0,
        logo: file_0 ?? null
      };
    });
  };
  const handleCourseCoverChange = async (file_1?: DataFileInterface) => {
    setTempState(prev_1 => {
      if (!prev_1) return prev_1;
      return {
        ...prev_1,
        image: file_1 ?? null
      };
    });
  };
  const handleCertificateLogoChange = async (file_2?: DataFileInterface) => {
    setTempState(prev_2 => {
      if (!prev_2) return prev_2;
      return {
        ...prev_2,
        certificateLogo: file_2 ?? null
      };
    });
  };
  const handleSignatureChange = async (file_3?: DataFileInterface) => {
    setTempState(prev_3 => {
      if (!prev_3) return prev_3;
      return {
        ...prev_3,
        signature: file_3 ?? null
      };
    });
  };
  const handleDomainChange = async (domainId?: DomainId) => {
    if (isModalWasOpenOnce) {
      setTempState(prev_4 => {
        if (!prev_4) return prev_4;
        return {
          ...prev_4,
          ...(domainId ? {
            domainId: domainId
          } : {})
        };
      });
    } else {
      domainConfirmationModal.open(domainId);
    }
  };
  const handleDomainChangeConfirm = async () => {
    setTempState(prev_5 => {
      if (!prev_5) return prev_5;
      return {
        ...prev_5,
        ...(domainConfirmationModal.data ? {
          domainId: domainConfirmationModal.data
        } : {})
      };
    });
  };
  const handleIssueCertificateUponCompletionChange = async (value: boolean) => {
    setTempState(prev_6 => {
      if (!prev_6) return prev_6;
      return {
        ...prev_6,
        issueCertificateUponCompletion: value ?? null
      };
    });
  };
  return {
    tempState,
    setTempState,
    courseData,
    errors,
    isFetching,
    handleTeacherPhotoChange,
    handleCourseLogoChange,
    handleCourseCoverChange,
    handleCertificateLogoChange,
    handleSignatureChange,
    handleIssueCertificateUponCompletionChange,
    domainConfirmationModal,
    handleDomainChange,
    handleDomainChangeConfirm,
    handleFormSubmit,
    isCodeValid,
    setIsCodeValid
  };
};